import { Capacitor } from '@capacitor/core';

// Quét mã vạch/QR cho nhóm VẬN HÀNH.
// - Android native: plugin ML Kit (Google code scanner — nhanh, không cần quyền camera).
// - iOS native + web: camera trong WebView + BarcodeDetector; máy/trình duyệt không có sẵn (Safari/WKWebView)
//   thì dùng bản zxing-wasm (tải lười, file .wasm đóng gói cùng app — không gọi CDN).
//   (Plugin ML Kit chưa hỗ trợ Swift Package Manager nên không có trên bản iOS.)
// Máy quét cầm tay kiểu bàn phím vẫn gõ thẳng vào ô tìm kiếm + Enter được.
// Trả Promise<string|null> (null = người dùng huỷ).

// Mã Codabar có ký tự start/stop trùng nhau ở 2 đầu (vd "A123A") -> bỏ đi (giống app i love GTS).
export function cleanCode(raw) {
  let s = String(raw || '').trim();
  if (s.length >= 2 && /^[A-Za-z]$/.test(s[0]) && s[0] === s[s.length - 1]) s = s.slice(1, -1);
  return s;
}

let beepAudio = null;
export function playOpsSound(name) {
  try {
    const a = name === 'beep' ? (beepAudio = beepAudio || new Audio('/sounds/ops/barcode_scanner_sound_effect.mp3')) : new Audio(`/sounds/ops/${name}.mp3`);
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch { /* ignore */ }
}

async function scanNative() {
  const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
  const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
  if (!available) {
    await BarcodeScanner.installGoogleBarcodeScannerModule();
    throw new Error('Đang tải bộ quét mã của Google, thử lại sau vài giây');
  }
  try {
    const { barcodes } = await BarcodeScanner.scan();
    return barcodes && barcodes[0] ? barcodes[0].rawValue || barcodes[0].displayValue || '' : null;
  } catch (e) {
    if (/cancel/i.test(e?.message || '')) return null;
    throw e;
  }
}

// BarcodeDetector có sẵn (Chrome/Android) hoặc bản zxing-wasm.
async function makeDetector() {
  if ('BarcodeDetector' in window) {
    try {
      const formats = await window.BarcodeDetector.getSupportedFormats();
      if (formats && formats.length) return new window.BarcodeDetector();
    } catch { /* rơi xuống bản wasm */ }
  }
  const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
    import('barcode-detector/ponyfill'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);
  prepareZXingModule({ overrides: { locateFile: (path, prefix) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) } });
  return new BarcodeDetector();
}

async function scanWeb() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Thiết bị này không mở được camera — hãy nhập mã hoặc dùng máy quét');
  }
  const detector = await makeDetector();
  return new Promise((resolve, reject) => {
    const wrap = document.createElement('div');
    wrap.className = 'ops-scan-overlay';
    wrap.innerHTML = `
      <video playsinline muted></video>
      <div class="ops-scan-window"></div>
      <div class="ops-scan-bar">
        <span>Đưa mã vạch / QR vào khung</span>
        <button type="button" class="btn ghost sm">Đóng</button>
      </div>`;
    document.body.appendChild(wrap);
    const video = wrap.querySelector('video');
    let stream = null;
    let done = false;
    let timer = null;
    const finish = (val, err) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      wrap.remove();
      if (err) reject(err);
      else resolve(val);
    };
    wrap.querySelector('button').onclick = () => finish(null);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((s) => {
        stream = s;
        video.srcObject = s;
        return video.play();
      })
      .then(() => {
        const tick = async () => {
          if (done) return;
          try {
            const codes = await detector.detect(video);
            if (codes && codes[0] && codes[0].rawValue) return finish(codes[0].rawValue);
          } catch { /* khung chưa sẵn sàng */ }
          timer = setTimeout(tick, 180);
        };
        tick();
      })
      .catch((e) => finish(null, new Error(`Không mở được camera: ${e.message}`)));
  });
}

export async function scanBarcode() {
  const nativeOk = Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('BarcodeScanner');
  const raw = nativeOk ? await scanNative() : await scanWeb();
  if (raw == null) return null;
  playOpsSound('beep');
  return cleanCode(raw);
}
